/**
 * Context Menu Icon Mapping
 *
 * Action id → lucide icon for the canvas context menus. Items without an
 * entry render label-only (no empty icon slot), matching the config's
 * optional `icon` contract.
 */

import {
    AppWindow,
    BoxSelect,
    Check,
    ClipboardPaste,
    Copy,
    CopyPlus,
    DoorOpen,
    EyeOff,
    Filter,
    FlipHorizontal,
    FlipHorizontal2,
    Focus,
    Group,
    Home,
    Link2,
    ListTree,
    Move,
    Move3d,
    Paintbrush,
    Pencil,
    PencilRuler,
    Pin,
    PinOff,
    RefreshCw,
    RotateCw,
    Ruler,
    Scissors,
    Search,
    SquareDashedBottom,
    Trash2,
    Undo2,
    X,
    ZoomIn,
    ZoomOut,
    type LucideIcon,
} from 'lucide-react';

const CONTEXT_MENU_ICONS: Record<string, LucideIcon> = {
    // Edit
    cancel: X,
    repeatLastCommand: RotateCw,
    copy: Copy,
    cut: Scissors,
    paste: ClipboardPaste,
    pasteAlignedSamePlace: ClipboardPaste,
    pasteAlignedSelectedLevels: ClipboardPaste,
    pasteAlignedSelectedViews: ClipboardPaste,
    delete: Trash2,
    selectAll: BoxSelect,
    createSimilar: CopyPlus,
    move: Move,
    rotate: RotateCw,
    mirrorPickAxis: FlipHorizontal2,
    mirrorDrawAxis: FlipHorizontal2,
    setOffset: Move3d,
    setRadius: Ruler,
    toggleChain: Link2,

    // Visibility / override
    filterSelection: Filter,
    hideElements: EyeOff,
    hideCategory: EyeOff,
    hideByFilter: EyeOff,
    isolateElement: Focus,
    isolateCategory: Focus,
    overrideByCategory: Paintbrush,
    overrideByElement: Paintbrush,
    overrideByFilter: Paintbrush,

    // Modify
    createGroup: Group,
    editType: Pencil,
    editFamily: PencilRuler,
    viewProperties: Pencil,
    showProperties: Pencil,
    pin: Pin,
    unpin: PinOff,
    flipHand: FlipHorizontal,
    flipFacing: FlipHorizontal,
    flipOrientation: FlipHorizontal,

    // Location line variants share one icon.
    setLocationWallCenterline: Ruler,
    setLocationCoreFaceInterior: Ruler,
    setLocationCoreFaceExterior: Ruler,
    setLocationFinishFaceInterior: Ruler,
    setLocationFinishFaceExterior: Ruler,

    // Sketch / placement
    activateWall: Link2,
    activateDoor: DoorOpen,
    activateWindow: AppWindow,
    finishWall: Check,
    restartWall: Undo2,
    makeWorkPlane: SquareDashedBottom,

    // Links / browser / find
    reloadLinkedCad: RefreshCw,
    browserOrganization: ListTree,
    openFind: Search,

    // View / zoom
    zoomToFit: ZoomIn,
    zoomToSelection: ZoomIn,
    zoomInRegion: ZoomIn,
    zoomOut: ZoomOut,
    zoomSheetSize: ZoomIn,
    viewCubeGoHome: Home,
    viewCubeLockToSelection: Focus,
    viewCubeSaveViewAsHome: Home,
    viewCubeToggleProjection: SquareDashedBottom,
    viewCubeOptions: Pencil,
};

/** Icon for a context-menu action id, or null when none is mapped. */
export function contextMenuIcon(actionId?: string): LucideIcon | null {
    if (!actionId) return null;
    return CONTEXT_MENU_ICONS[actionId] ?? null;
}
